<form #f="ngForm" (ngSubmit)="submit(f)" novalidate>
  <nb-card class="dialog-card" [nbSpinner]="saving">
    <nb-card-header>
      <h4>{{ (isEdit ? "pages.mcp.form.editTitle" : "pages.mcp.form.newTitle") | translate }}</h4>
    </nb-card-header>

    <nb-card-body>
      <!-- Start from the public directory (official MCP Registry). -->
      <div class="registry" *ngIf="!isEdit">
        <label class="label">
          {{ "pages.mcp.registry.label" | translate }}
          <nb-icon icon="question-mark-circle-outline" class="hint-icon"
            nbPopover="{{ 'pages.mcp.registry.hint' | translate }}" nbPopoverTrigger="hover" nbPopoverPlacement="right"></nb-icon>
        </label>
        <app-search-input [value]="registryQuery" (valueChange)="onRegistryQuery($event)"
          placeholderKey="pages.mcp.registry.placeholder"></app-search-input>
        <p class="registry__status" *ngIf="registryLoading" aria-live="polite">
          <nb-icon icon="loader-outline" class="spin"></nb-icon>
          {{ "pages.mcp.registry.searching" | translate }}
        </p>
        <p class="hint hint--warning" *ngIf="registryError">{{ registryError | translate }}</p>
        <div class="registry__results" *ngIf="!registryLoading && registryResults">
          <nb-list *ngIf="registryResults?.length">
            <nb-list-item *ngFor="let e of registryResults; trackBy: trackEntry">
              <div class="entry">
                <div class="entry__main">
                  <div class="entry__head">
                    <strong>{{ e.title }}</strong>
                    <span class="badge badge--ok" *ngIf="e.official" [nbTooltip]="'pages.mcp.registry.officialTip' | translate">
                      {{ "pages.mcp.registry.official" | translate }}
                    </span>
                    <span class="badge" [class.badge--warn]="!e.auth.known"
                      [nbTooltip]="(e.auth.known ? 'pages.mcp.registry.authKnownTip' : 'pages.mcp.registry.authUnknownTip') | translate">
                      {{ (e.auth.known ? "pages.mcp.presets." + e.auth.preset : "pages.mcp.registry.authUnknown") | translate }}
                    </span>
                  </div>
                  <span class="entry__domain">{{ e.domain }}</span>
                  <p class="entry__desc">{{ e.description }}</p>
                </div>
                <button nbButton size="small" status="primary" type="button" (click)="pick(e)"
                  [nbTooltip]="'pages.mcp.registry.pickTip' | translate">
                  {{ "pages.mcp.registry.pick" | translate }}
                </button>
              </div>
            </nb-list-item>
          </nb-list>
          <p class="hint" *ngIf="!registryResults.length && !registryError && !registryCursor">
            {{ "pages.mcp.registry.empty" | translate }}
          </p>
          <p class="hint" *ngIf="!registryResults.length && !registryError && registryCursor">
            {{ "pages.mcp.registry.noneOnPage" | translate }}
          </p>
          <p class="registry__status" *ngIf="registryLoadingMore" aria-live="polite">
            <nb-icon icon="loader-outline" class="spin"></nb-icon>
            {{ "pages.mcp.registry.searching" | translate }}
          </p>
          <button *ngIf="registryCursor && !registryLoadingMore" nbButton size="small" status="basic" type="button"
            class="registry__more" (click)="loadMore()" [nbTooltip]="'pages.mcp.registry.moreTip' | translate">
            {{ "pages.mcp.registry.more" | translate }}
          </button>
        </div>
        <div class="picked" *ngIf="picked">
          <nb-icon icon="checkmark-circle-2-outline"></nb-icon>
          <span>{{ "pages.mcp.registry.picked" | translate: { name: picked.title, domain: picked.domain } }}</span>
        </div>
      </div>

      <div class="form-field">
        <label class="label">
          {{ "pages.mcp.fields.serverUrl" | translate }} *
          <nb-icon icon="question-mark-circle-outline" class="hint-icon"
            nbPopover="{{ 'pages.mcp.hints.serverUrl' | translate }}" nbPopoverTrigger="hover" nbPopoverPlacement="right"></nb-icon>
        </label>
        <input nbInput fullWidth fieldSize="small" name="server_url" required type="url" pattern="https://.+"
          [(ngModel)]="serverUrl" (paste)="onUrlPaste($event)" placeholder="https://mcp.example.org/mcp" />
        <span class="hint" *ngFor="let n of pasteNotes"
          [class.hint--warning]="n === 'enterKey' || n === 'localOnly' || n === 'noUrl'">
          {{ "pages.mcp.paste.notes." + n | translate }}
        </span>
      </div>

      <p class="hint hint--warning" *ngIf="picked && !picked.auth.known">
        {{ "pages.mcp.registry.authUnknownNote" | translate }}
      </p>

      <div class="form-field">
        <label class="label">
          {{ "pages.mcp.fields.authType" | translate }}
          <nb-icon icon="question-mark-circle-outline" class="hint-icon"
            nbPopover="{{ 'pages.mcp.hints.authType' | translate }}" nbPopoverTrigger="hover" nbPopoverPlacement="right"></nb-icon>
        </label>
        <nb-select fullWidth size="small" name="preset" [(selected)]="preset">
          <nb-option *ngFor="let p of presets" [value]="p">{{ "pages.mcp.presets." + p | translate }}</nb-option>
        </nb-select>
      </div>

      <div class="form-field" *ngIf="preset === 'custom'">
        <label class="label">
          {{ "pages.mcp.fields.authHeaderName" | translate }} *
          <nb-icon icon="question-mark-circle-outline" class="hint-icon"
            nbPopover="{{ 'pages.mcp.hints.headerName' | translate }}" nbPopoverTrigger="hover" nbPopoverPlacement="right"></nb-icon>
        </label>
        <input nbInput fullWidth fieldSize="small" name="header_name" required maxlength="100" pattern="[A-Za-z0-9-]+"
          [(ngModel)]="headerName" placeholder="X-Auth-Token" />
      </div>

      <div class="form-field" *ngIf="preset !== 'none'">
        <label class="label">
          {{ "pages.mcp.fields.authSecret" | translate }} *
          <nb-icon icon="question-mark-circle-outline" class="hint-icon"
            nbPopover="{{ ('pages.mcp.hints.secretFor.' + (preset === 'bearer' ? 'bearer' : 'header')) | translate }}"
            nbPopoverTrigger="hover" nbPopoverPlacement="right"></nb-icon>
        </label>
        <input nbInput fullWidth fieldSize="small" type="password" name="auth_secret" autocomplete="off"
          [(ngModel)]="authSecret" [class.al-required-missing]="f.submitted && secretMissing"
          [placeholder]="(hasStoredSecret ? 'pages.mcp.fields.secretStored' : 'pages.mcp.fields.secretPlaceholder') | translate" />
        <span class="hint">
          {{ (preset === "bearer" ? "pages.mcp.preview.bearer" : "pages.mcp.preview.header") | translate: { header: sentHeader } }}
        </span>
        <span class="hint" *ngIf="picked?.auth?.hint">
          {{ "pages.mcp.registry.providerHint" | translate: { hint: picked!.auth.hint } }}
        </span>
        <nb-checkbox *ngIf="connector?.has_auth_secret" name="clear_secret" [(ngModel)]="clearSecret"
          [nbTooltip]="'pages.mcp.tooltips.clearSecret' | translate">
          {{ "pages.mcp.fields.clearSecret" | translate }}
        </nb-checkbox>
      </div>

      <button type="button" class="advanced-toggle" (click)="showAdvanced = !showAdvanced"
        [nbTooltip]="'pages.mcp.tooltips.advanced' | translate">
        <nb-icon [icon]="showAdvanced ? 'chevron-down-outline' : 'chevron-right-outline'"></nb-icon>
        {{ "pages.mcp.form.advanced" | translate }}
      </button>

      <div class="form-field" *ngIf="showAdvanced">
        <label class="label">
          {{ "pages.mcp.fields.name" | translate }}
          <nb-icon icon="question-mark-circle-outline" class="hint-icon"
            nbPopover="{{ 'pages.mcp.hints.name' | translate }}" nbPopoverTrigger="hover" nbPopoverPlacement="right"></nb-icon>
        </label>
        <input nbInput fullWidth fieldSize="small" name="name" maxlength="120" [(ngModel)]="name"
          [placeholder]="'pages.mcp.fields.namePlaceholder' | translate" />
      </div>

      <div class="grid-3" *ngIf="showAdvanced">
        <div class="form-field">
          <label class="label">
            {{ "pages.mcp.fields.timeout" | translate }}
            <nb-icon icon="question-mark-circle-outline" class="hint-icon"
              nbPopover="{{ 'pages.mcp.hints.timeout' | translate }}" nbPopoverTrigger="hover"></nb-icon>
          </label>
          <input nbInput fullWidth fieldSize="small" type="number" name="timeout" min="3" max="60" required
            [(ngModel)]="timeoutSeconds" />
        </div>
        <div class="form-field">
          <label class="label">
            {{ "pages.mcp.fields.maxCalls" | translate }}
            <nb-icon icon="question-mark-circle-outline" class="hint-icon"
              nbPopover="{{ 'pages.mcp.hints.maxCalls' | translate }}" nbPopoverTrigger="hover"></nb-icon>
          </label>
          <input nbInput fullWidth fieldSize="small" type="number" name="max_calls" min="1" max="10" required
            [(ngModel)]="maxCallsPerTurn" />
        </div>
        <div class="form-field">
          <label class="label">
            {{ "pages.mcp.fields.maxChars" | translate }}
            <nb-icon icon="question-mark-circle-outline" class="hint-icon"
              nbPopover="{{ 'pages.mcp.hints.maxChars' | translate }}" nbPopoverTrigger="hover"></nb-icon>
          </label>
          <input nbInput fullWidth fieldSize="small" type="number" name="max_chars" min="500" max="50000" required
            [(ngModel)]="maxResultChars" />
        </div>
      </div>

      <p class="hint" *ngIf="isEdit">{{ "pages.mcp.form.editNote" | translate }}</p>

      <!-- One sentence per failure kind, the technical reason below it. -->
      <div class="error" *ngIf="errorKind">
        <strong>{{ ("pages.mcp.errors.kinds." + errorKind) | translate }}</strong>
        <span class="error__detail">{{ errorDetail }}</span>
      </div>
    </nb-card-body>

    <nb-card-footer>
      <button nbButton status="basic" size="small" type="button" (click)="close()">
        {{ "buttons.cancel" | translate }}
      </button>
      <button nbButton status="primary" size="small" type="submit" [disabled]="saving">
        {{ (isEdit ? "buttons.save" : "pages.mcp.form.connect") | translate }}
      </button>
    </nb-card-footer>
  </nb-card>
</form>
